import * as KatalMetrics from '@amzn/katal-metrics';
import KatalMetricsDriverSushi from '@amzn/katal-metrics-driver-sushi';
import KatalMetricsDriverArrayCollector from '@amzn/katal-metrics/lib/driver/KatalMetricsDriverArrayCollector';
import { NodeEnv } from 'src/constants';
import { REALM, SITE } from '../constants';

// eslint-disable-next-line no-console
const metricsConsoleErrorHandler = (err: Error) => console.error(err);

const makeMetricsDriver = (): KatalMetrics.MetricsDriver => {
  if (process.env.NODE_ENV !== NodeEnv.production) {
    const metricsDriver = new KatalMetricsDriverArrayCollector();
    //  Attach to global window object so tests can see it
    (window as any).metricsDriver = metricsDriver;
    return metricsDriver;
  }

  return new KatalMetricsDriverSushi.Builder()
    .withDomainRealm('prod', REALM['eu-west-1'])
    .withErrorHandler(metricsConsoleErrorHandler)
    .build();
};
const makePublisher = (overrideMetricsToProd): KatalMetrics.Publisher => {
  const metricsDriver = makeMetricsDriver();
  const initialMetricsContext = new KatalMetrics.Context.Builder()
    .withSite(
      overrideMetricsToProd
        ? 'AACChrome.Prod'
        : SITE['prod'] || 'AACChrome',
    )
    .withServiceName('BigTopSharedWidgetService')
    .build();
  return new KatalMetrics.Publisher(
    metricsDriver,
    metricsConsoleErrorHandler,
    initialMetricsContext,
  );
};
export const KatalMetricsPublisher = makePublisher(false);
export const KatalProdOverrideMetricsPublisher = makePublisher(true);

export const AjaxMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'BigTopAjaxService.getAccounts',
  );
export const AjaxLandingMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'BigTopAjaxService.setLanding',
  );
export const UseAcesBannersMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('useAcesBanners');

export const UseStardustBannersMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('useStardustBanners');

export const ConsoleLevelBannerMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('ConsoleLevelBanner');
export const PageLevelBannerMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('PageLevelBanner');
export const CarouselMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('CarouselModal');

export const LanguageSwitchMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('LanguageSwitching');
export const HelpDropdownPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('HelpDropdown');
export const AccountSwitcherPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('AccountSwitcher');
export const RegionSwitcherPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('RegionSwitcher');
export const UserDropdownPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('UserDropdown');
export const MobileNavPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('MobileNav');
export const DesktopNavPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('DesktopNav');
export const ChromeConfigurationPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('ChromeConfiguration');
export const FooterViewSwitcherPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('FooterViewSwitcher');
export const CTCIPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('CTCI');
export const HeaderMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('Header');
export const KatalProdOverrideHeaderMetricPublisher =
  KatalProdOverrideMetricsPublisher.newChildActionPublisherForMethod('Header');
export const FooterMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('Footer');
export const KatalProdOverrideFooterMetricPublisher =
  KatalProdOverrideMetricsPublisher.newChildActionPublisherForMethod('Footer');
export const PivotNavMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('PivotNav');
export const NavMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('Nav');
export const KatalProdOverridePivotNavMetricPublisher =
  KatalProdOverrideMetricsPublisher.newChildActionPublisherForMethod(
    'PivotNav',
  );
export const KatalProdOverrideNavMetricPublisher =
  KatalProdOverrideMetricsPublisher.newChildActionPublisherForMethod('Nav');
export const ChromeBundleMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('ChromeJsBundle');
export const ChromeApiMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('ChromeApi');
export const InternalChromeApiMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('InternalChromeApi');
export const AcesAjaxMetricPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('AcesAjax');
export const CountrySwitcherPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('CountrySwitcher');
export const CurrencySwitcherPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('CurrencySwitcher');
export const FeedbackTriggerPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('FeedbackTrigger');
export const CommunicationsDrawerPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'CommunicationsDrawer',
  );
export const MobileCommuCommunicationsDrawerPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'MobileCommunicationsDrawer',
  );
export const ErrorBoundaryPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('ErrorBoundary');

export const AjaxRequestPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('ChromeAjaxRequest');
export const UseStardustPageBannersMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'useStardustPageBanners',
  );

export const TrimMarketplaceSwitcherPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'TrimMarketplaceSwitcher',
  );

export const CampaignCreationEligibilityPublisher: KatalMetrics.Publisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod(
    'CampaignCreationEligibility',
  );
